import * as z from 'zod/v4';
import { db } from '@/lib/firebase';
import {
  doc,
  collection,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  where,
  type QueryConstraint,
} from 'firebase/firestore';
import type { CollectionReference, Query } from "firebase/firestore";
import { PrivacyRequestDoc, PrivacyRequestDocSchema } from '@/services/privacyRequestTrackerV2';
import type { PrivacyRequestWithId } from '@/services/privacyRequestTrackerV2';

export const PrivacyRequestTrackerNoteDocSchema = z.object({
  owner: z.string(),
  message: z.string(),
  metadata: z.object({
    created_at: z.date().default(new Date()),
    deleted_at: z.date().nullable().default(null),
  }).default({}),
});
export type PrivacyRequestTrackerNote = z.infer<typeof PrivacyRequestTrackerNoteDocSchema>;
export type PrivacyRequestTrackerNoteDocWithId = PrivacyRequestTrackerNote & { id: string };

export const PrivacyRequestTrackerNoteUpdateSchema = PrivacyRequestTrackerNoteDocSchema.partial().extend({
  id: z.string(),
});

export type PrivacyRequestTrackerNoteUpdate = z.infer<typeof PrivacyRequestTrackerNoteUpdateSchema>;

function normalizeFromFirestore(data: any): PrivacyRequestTrackerNote {
  const created_at = data.metadata?.created_at?.toDate?.() ?? data.metadata?.created_at;
  const deleted_at = data.metadata?.deleted_at?.toDate?.() ?? data.metadata?.deleted_at ?? null;

  return PrivacyRequestTrackerNoteDocSchema.parse({
    ...data,
    metadata: {
      created_at,
      deleted_at,
    },
  });
}

/**
 * Helper: get the privacyRequestNote collection for a given customer
 * /customers/{customerId}/privacyRequests/{requestId}
 */
function privacyRequestNotesCollection(customerId: string, privacyRequestId: string) {
  const customerDocRef = doc(db, "customers", customerId);
  const colRef = collection(customerDocRef, "privacyRequests", privacyRequestId, "notes") as CollectionReference<PrivacyRequestTrackerNoteDocWithId>;
  return colRef;
}

export async function read(
  customer: string,
  privacyRequest: PrivacyRequestWithId,
  noteId: string | null = null,
  deleted: boolean = false,
): Promise<
  PrivacyRequestTrackerNoteDocWithId |
  PrivacyRequestTrackerNoteDocWithId[] |
  null
> {
  const prCol = privacyRequestNotesCollection(customer, privacyRequest.id);

  // --- Read specific note ---
  if (noteId) {
    const prDocRef = doc(prCol, noteId);
    const snap = await getDoc(prDocRef);
    if (!snap.exists()) return null;

    const parsed = normalizeFromFirestore(snap.data());
    return { id: snap.id, ...parsed } as PrivacyRequestTrackerNoteDocWithId;
  }

  // --- Read all notes (with deleted filter + sort) ---
  const col = deleted
    ? prCol // no filter → return all docs
    : query(prCol, where("metadata.deleted_at", "==", null));

  const snap = await getDocs(col);

  // --- Parse notes ---
  let notes = snap.docs.map((d) => {
    const parsed = normalizeFromFirestore(d.data());
    return { id: d.id, ...parsed } as PrivacyRequestTrackerNoteDocWithId;
  });

  // --- Sort in JS by created_at DESC ---
  notes.sort((a, b) => {
    const tA = a.metadata.created_at?.getTime() ?? 0;
    const tB = b.metadata.created_at?.getTime() ?? 0;
    return tB - tA;
  });

  return notes;
}

//
// CREATE (returns new ID)
//
export async function create(
  customer: string,
  privacyRequest: PrivacyRequestWithId,
  item: PrivacyRequestTrackerNote
): Promise<PrivacyRequestTrackerNoteDocWithId> {
  const prCol = privacyRequestNotesCollection(customer, privacyRequest.id);
  console.log('asd', prCol);

  // Validate input with Zod (and apply defaults)
  const validated = PrivacyRequestTrackerNoteDocSchema.parse(item);

  const now = new Date();
  const toSave: PrivacyRequestTrackerNote = {
    ...validated,
    metadata: {
      created_at: validated.metadata?.created_at ?? now,
      deleted_at: validated.metadata?.deleted_at ?? null,
    },
  };

  const docRef = await addDoc(prCol, toSave);

  return { id: docRef.id, ...toSave } as PrivacyRequestTrackerNoteDocWithId;
}

//
// UPDATE (partial update, requires id)
//
export async function update(
  customer: string,
  privacyRequest: PrivacyRequestWithId,
  item: PrivacyRequestTrackerNoteDocWithId
): Promise<PrivacyRequestTrackerNoteDocWithId> {
  const parsed = PrivacyRequestTrackerNoteUpdateSchema.parse(item);
  const prCol = privacyRequestNotesCollection(customer, privacyRequest.id);

  const { id, ...updateData } = parsed;

  const prDocRef = doc(prCol, id);
  await updateDoc(prDocRef, updateData as any);

  const snap = await getDoc(prDocRef);
  if (!snap.exists()) {
    throw new Error(`Privacy request note ${id} not found after update`);
  }

  const validated = normalizeFromFirestore(snap.data());
  return { id, ...validated } as PrivacyRequestTrackerNoteDocWithId;
}

//
// DELETE
//
export async function remove(
  customer: string,
  privacyRequest: PrivacyRequestWithId,
  item: PrivacyRequestTrackerNoteDocWithId | string,
  soft: boolean = true
): Promise<{ id: string }> {
  const id = typeof item === 'string' ? item : item.id;
  if (!id) throw new Error('remove() requires an id');

  const prCol = privacyRequestNotesCollection(customer, privacyRequest.id);
  const prDocRef = doc(prCol, id);

  if(soft) {
    await updateDoc(prDocRef, {
      "metadata.deleted_at": new Date(),
    });

    return { id };
  }

  await deleteDoc(prDocRef);

  return { id };
}
